import {
	MessageSquare as CommentIcon,
	Compass,
	type LucideIcon,
	Plus,
	Search,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { type Dispatch, type SetStateAction, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { StudioToolPicker } from "./StudioToolPicker";

// --- Bottom Action Buttons ---
interface ActionButton {
	id: string;
	icon: LucideIcon;
	label: string;
	primary?: boolean;
}

const BOTTOM_ACTIONS: ActionButton[] = [
	{ id: "chat", icon: CommentIcon, label: "Chat", primary: true },
	{ id: "compass", icon: Compass, label: "Explore" },
	{ id: "search", icon: Search, label: "Search" },
	{ id: "add", icon: Plus, label: "Add" },
];

interface CanvasActionBarProps {
	isChatOpen: boolean;
	setIsChatOpen: Dispatch<SetStateAction<boolean>>;
	isToolPickerOpen: boolean;
	setIsToolPickerOpen: Dispatch<SetStateAction<boolean>>;
	isModalOpen: boolean;
	setIsModalOpen: Dispatch<SetStateAction<boolean>>;
	onToolClick: (toolSlug: string | null, label: string) => void;
	loadingTool: string | null;
}

/** Bottom-center action bar: chat toggle, studio tool picker, canvas search, upload. */
export function CanvasActionBar({
	isChatOpen,
	setIsChatOpen,
	isToolPickerOpen,
	setIsToolPickerOpen,
	isModalOpen,
	setIsModalOpen,
	onToolClick,
	loadingTool,
}: CanvasActionBarProps) {
	const [isToolInputOpen, setIsToolInputOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const searchInputRef = useRef<HTMLInputElement>(null);

	return (
		<div className="absolute bottom-0 left-0 right-0 flex items-center justify-center pb-5 pt-3 pointer-events-none z-20">
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.4, delay: 0.4 }}
				className="relative flex items-center gap-2 pointer-events-auto bg-bg-surface border border-border-subtle rounded-full p-1"
			>
				{/* Studio Tool Picker popup */}
				<StudioToolPicker
					isOpen={isToolPickerOpen}
					onClose={() => setIsToolPickerOpen(false)}
					onToolClick={onToolClick}
					loadingTool={loadingTool}
				/>

				{/* Search Input popup */}
				<AnimatePresence>
					{isToolInputOpen && (
						<motion.div
							initial={{ opacity: 0, y: 10, scale: 0.95 }}
							animate={{ opacity: 1, y: 0, scale: 1 }}
							exit={{ opacity: 0, y: 10, scale: 0.95 }}
							transition={{ duration: 0.2, ease: "easeOut" }}
							className="absolute bottom-full mb-3 -left-[110px] -translate-x-1/2 w-[420px]"
						>
							<div className="bg-bg-surface border border-border-subtle rounded-xl shadow-popover px-4 py-3 flex items-center gap-3 focus-within:ring-2 focus-within:ring-focus-ring">
								<Search
									size={16}
									className="text-text-tertiary flex-shrink-0"
								/>
								<input
									ref={searchInputRef}
									type="text"
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									placeholder="Search canvas..."
									className="flex-1 bg-transparent text-left text-text-primary placeholder:text-text-tertiary text-sm focus:outline-none"
									onKeyDown={(e) => {
										if (e.key === "Escape") {
											setIsToolInputOpen(false);
										}
									}}
								/>
								{searchQuery && (
									<Button
										variant="ghost"
										size="icon-xs"
										onClick={() => setSearchQuery("")}
									>
										<span className="text-xs">✕</span>
									</Button>
								)}
							</div>
						</motion.div>
					)}
				</AnimatePresence>

				{BOTTOM_ACTIONS.map((action) => {
					const Icon = action.icon;
					// Chat button: active tint when sidebar open
					const isChatActive = action.id === "chat" && isChatOpen;
					// compass button: active tint when picker open
					const isCompassActive =
						action.id === "compass" && isToolPickerOpen;
					const isAddActive = action.id === "add" && isModalOpen;
					const isSearchActive =
						action.id === "search" && isToolInputOpen;
					const isActive =
						isChatActive ||
						isCompassActive ||
						isAddActive ||
						isSearchActive;
					return (
						<button
							key={action.id}
							className={cn(
								"w-11 h-11 rounded-full flex items-center justify-center transition-all shadow-lg cursor-pointer",
								isActive
									? "bg-nav-active-bg text-text-primary"
									: "backdrop-blur-sm border border-border-subtle text-text-primary",
							)}
							title={action.label}
							onClick={() => {
								if (action.id === "chat") setIsChatOpen((v) => !v);
								if (action.id === "compass")
									setIsToolPickerOpen((v) => !v);
								if (action.id === "add") setIsModalOpen((v) => !v);
								if (action.id === "search") {
									setIsToolInputOpen((v) => {
										const next = !v;
										if (next) {
											setTimeout(
												() => searchInputRef.current?.focus(),
												100,
											);
										}
										return next;
									});
								}
							}}
							onMouseDown={(e) => e.stopPropagation()}
						>
							<Icon size={18} />
						</button>
					);
				})}
			</motion.div>
		</div>
	);
}
